import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, EMPTY, filter, map, Observable, of, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { AsyncRequestStatus } from '@portal/shared/store/request-status';

import { DateUtil } from '@portal/shared/utils/date-util';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { DeviceDetailsMetricsActions } from '../../../store/device-details-metrics.actions';
import * as DeviceDetailsMetricsReducer from '../../../store/device-details-metrics.reducer';
import { MeterElectricalTabContentActions } from './meter-electrical-tab-content.actions';
import * as MeterElectricalTabContentReducer from './meter-electrical-tab-content.reducer';
import { MeterElectricalTabContentService } from '../meter-electrical-tab-content.service';
import { ExportRow } from '@portal/shared/types/export.types';
import { ListingPageModel } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingPayload } from '@portal/shared/types/listing.types';
import {
  ELECTRICAL_FIELD_TO_FILTER_TYPE,
  ElectricalChartFilterType,
  GetChartDataFilter,
  GetChartDataPayload,
  GetFiltersPayload,
  GetTableDataPayload,
  GetTableDataResponse
} from '../meter-electrical-tab-content.types';
import { Commodity } from '@portal/shared/types/commodity.types';
import { DEVICE_DETAILS_METRICS } from '../../../device-details-metrics.types';
import { DatepickerRange } from '@ui/aos-lib';
import { DeviceDetailsPage } from '../../../../../devices.models';
import { ExportUtilsService } from 'src/app/services/utility/export-utils.service';
import { ExportFacadeService } from 'src/app/services/utility/export-facade.service';

function fetchFilters(
  tzService: TimezoneService,
  service: MeterElectricalTabContentService,
  dateRange: DatepickerRange,
  deviceDetails: DeviceDetailsPage
): Observable<Action> {
  const { start, end } = tzService.getDatepickerRange({
    start: dateRange.start,
    end: dateRange.end
  });
  const payload: GetFiltersPayload = {
    deviceUID: deviceDetails.deviceUID as string,
    startDateUtc: DateUtil.toISOString(start),
    endDateUtc: DateUtil.addOneDayToString(end)
  };
  return service.getFilters(payload).pipe(
    map(filters => MeterElectricalTabContentActions.loadFiltersSuccess({ filters })),
    catchError(() => of(MeterElectricalTabContentActions.loadFiltersFailure({ error: 'Failed to load filters' })))
  );
}

export const loadFilters$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    service = inject(MeterElectricalTabContentService)
  ) =>
    actions$.pipe(
      ofType(DeviceDetailsMetricsActions.setDeviceDetails, DeviceDetailsMetricsActions.setDateRange),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails),
        store.select(DeviceDetailsMetricsReducer.selectActiveMetricsTab)
      ),
      filter(
        ([, dateRange, deviceDetails, activeTab]) =>
          dateRange !== null &&
          deviceDetails !== null &&
          deviceDetails.commodity === Commodity.Electricity &&
          activeTab === DEVICE_DETAILS_METRICS.Electrical
      ),
      switchMap(([, dateRange, deviceDetails]) =>
        fetchFilters(tzService, service, dateRange!, deviceDetails!).pipe(
          startWith(MeterElectricalTabContentActions.loadFilters())
        )
      )
    ),
  { functional: true }
);

export const loadFiltersOnMetricsTabSwitch$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    service = inject(MeterElectricalTabContentService)
  ) =>
    actions$.pipe(
      ofType(DeviceDetailsMetricsActions.setActiveMetricsTab),
      filter(({ activeMetricsTab }) => activeMetricsTab === DEVICE_DETAILS_METRICS.Electrical),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails)
      ),
      filter(
        ([, dateRange, deviceDetails]) =>
          dateRange !== null && deviceDetails !== null && deviceDetails.commodity === Commodity.Electricity
      ),
      switchMap(([, dateRange, deviceDetails]) => fetchFilters(tzService, service, dateRange!, deviceDetails!))
    ),
  { functional: true }
);

export const loadData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(MeterElectricalTabContentActions.filterChanged, MeterElectricalTabContentActions.loadFiltersSuccess),
      withLatestFrom(store.select(MeterElectricalTabContentReducer.selectShouldShowChart)),
      switchMap(([, shouldShowChart]) =>
        shouldShowChart
          ? of(MeterElectricalTabContentActions.loadChartData())
          : of(MeterElectricalTabContentActions.loadTableData())
      )
    ),
  { functional: true }
);

export const loadDataOnTabSwitch$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(MeterElectricalTabContentActions.selectTabType),
      withLatestFrom(
        store.select(MeterElectricalTabContentReducer.selectShouldShowChart),
        store.select(MeterElectricalTabContentReducer.selectChartDataRequest),
        store.select(MeterElectricalTabContentReducer.selectTableDataRequest)
      ),
      switchMap(([, shouldShowChart, chartStatus, tableStatus]) => {
        if (shouldShowChart && chartStatus.status === AsyncRequestStatus.Idle) {
          return of(MeterElectricalTabContentActions.loadChartData());
        }
        if (!shouldShowChart && tableStatus.status === AsyncRequestStatus.Idle) {
          return of(MeterElectricalTabContentActions.loadTableData());
        }
        return EMPTY;
      })
    ),
  { functional: true }
);

export const loadChartData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    service = inject(MeterElectricalTabContentService)
  ) =>
    actions$.pipe(
      ofType(MeterElectricalTabContentActions.loadChartData),
      withLatestFrom(
        store.select(MeterElectricalTabContentReducer.selectFilters),
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails)
      ),
      switchMap(([, filters, dateRange, deviceDetails]) => {
        if (dateRange === null || deviceDetails === null) {
          return EMPTY;
        }

        if (!filters?.length || filters.some(f => f.selected.length === 0)) {
          return of(MeterElectricalTabContentActions.loadChartDataSuccess({ healthData: [] }));
        }

        const { start, end } = tzService.getDatepickerRange({
          start: dateRange.start,
          end: dateRange.end
        });

        const dateFilter: GetChartDataFilter = {
          filterType: ElectricalChartFilterType.DateTimeUTC,
          filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
        };

        const deviceFilter: GetChartDataFilter = {
          filterType: ElectricalChartFilterType.DeviceUID,
          filteredValues: [deviceDetails.deviceUID as string]
        };

        const filterEntries: GetChartDataFilter[] = filters
          .filter(f => ELECTRICAL_FIELD_TO_FILTER_TYPE[f.filterType])
          .map(f => ({
            filterType: ELECTRICAL_FIELD_TO_FILTER_TYPE[f.filterType],
            filteredValues: f.selected.map(item => item.value as string)
          }));

        const payload: GetChartDataPayload = {
          filters: [dateFilter, deviceFilter, ...filterEntries]
        };

        return service.getChartData(payload).pipe(
          map(response =>
            MeterElectricalTabContentActions.loadChartDataSuccess({
              healthData: service.mapChartDataResponse(response)
            })
          ),
          catchError(() =>
            of(MeterElectricalTabContentActions.loadChartDataFailure({ error: 'Failed to load chart data' }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadTableData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    service = inject(MeterElectricalTabContentService)
  ) =>
    actions$.pipe(
      ofType(MeterElectricalTabContentActions.loadTableData),
      withLatestFrom(
        store.select(MeterElectricalTabContentReducer.selectFilters),
        store.select(MeterElectricalTabContentReducer.selectTableState),
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails)
      ),
      switchMap(([, filters, tableState, dateRange, deviceDetails]) => {
        if (dateRange === null || deviceDetails === null) {
          return EMPTY;
        }

        if (!filters?.length || filters.some(f => f.selected.length === 0)) {
          return of(
            MeterElectricalTabContentActions.loadTableDataSuccess({
              tableData: { columnDefs: [], data: [], total: 0 }
            })
          );
        }

        const { start, end } = tzService.getDatepickerRange({
          start: dateRange.start,
          end: dateRange.end
        });

        const dateFilter: GetChartDataFilter = {
          filterType: ElectricalChartFilterType.DateTimeUTC,
          filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
        };

        const deviceFilter: GetChartDataFilter = {
          filterType: ElectricalChartFilterType.DeviceUID,
          filteredValues: [deviceDetails.deviceUID as string]
        };

        const filterEntries: GetChartDataFilter[] = filters
          .filter(f => ELECTRICAL_FIELD_TO_FILTER_TYPE[f.filterType])
          .map(f => ({
            filterType: ELECTRICAL_FIELD_TO_FILTER_TYPE[f.filterType],
            filteredValues: f.selected.map(item => item.value as string)
          }));

        const sortDescriptor = tableState?.sort?.[0];
        const payload: GetTableDataPayload = {
          filters: [dateFilter, deviceFilter, ...filterEntries],
          sort: {
            columnName: 'DateTimeUTC',
            orderDirection: sortDescriptor?.dir === 'asc' ? 'ASC' : 'DESC'
          },
          skip: tableState?.skip ?? 0,
          take: tableState?.take ?? 25
        };

        return service.getTableData(payload).pipe(
          map(response =>
            MeterElectricalTabContentActions.loadTableDataSuccess({
              tableData: service.mapTableDataResponse(response)
            })
          ),
          catchError(() =>
            of(MeterElectricalTabContentActions.loadTableDataFailure({ error: 'Failed to load table data' }))
          )
        );
      })
    ),
  { functional: true }
);

export const reloadOnTableStateChange$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(MeterElectricalTabContentActions.tableStateChanged),
      withLatestFrom(store.select(MeterElectricalTabContentReducer.selectShouldShowTable)),
      filter(([, shouldShowTable]) => shouldShowTable),
      map(() => MeterElectricalTabContentActions.loadTableData())
    ),
  { functional: true }
);

export const export$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    service = inject(MeterElectricalTabContentService),
    exportUtils = inject(ExportUtilsService),
    exportFacade = inject(ExportFacadeService)
  ) =>
    actions$.pipe(
      ofType(MeterElectricalTabContentActions.exportFile),
      withLatestFrom(
        store.select(MeterElectricalTabContentReducer.selectTableData),
        store.select(MeterElectricalTabContentReducer.selectFilters),
        store.select(MeterElectricalTabContentReducer.selectTableState),
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails)
      ),
      filter(([, , , , dateRange, deviceDetails]) => dateRange !== null && deviceDetails !== null),
      switchMap(([{ fileType }, tableData, filters, tableState, dateRange, deviceDetails]) => {
        const { start, end } = tzService.getDatepickerRange({
          start: dateRange!.start,
          end: dateRange!.end
        });

        const dateFilter: GetChartDataFilter = {
          filterType: ElectricalChartFilterType.DateTimeUTC,
          filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
        };

        const deviceFilter: GetChartDataFilter = {
          filterType: ElectricalChartFilterType.DeviceUID,
          filteredValues: [deviceDetails!.deviceUID as string]
        };

        const filterEntries: GetChartDataFilter[] = (filters ?? [])
          .filter(f => ELECTRICAL_FIELD_TO_FILTER_TYPE[f.filterType])
          .map(f => ({
            filterType: ELECTRICAL_FIELD_TO_FILTER_TYPE[f.filterType],
            filteredValues: f.selected.map(item => item.value as string)
          }));

        const sortDescriptor = tableState?.sort?.[0];
        const exportPayload: GetTableDataPayload = {
          filters: [dateFilter, deviceFilter, ...filterEntries],
          sort: {
            columnName: 'DateTimeUTC',
            orderDirection: sortDescriptor?.dir === 'asc' ? 'ASC' : 'DESC'
          },
          skip: 0,
          take: 25
        };

        const serialNumber = deviceDetails!.serialNumber ?? deviceDetails!.deviceUID;

        const listingPageModel = {
          dataSource: {
            getAll: (payload: GetTableDataPayload) => service.getTableData(payload),
            total: 0
          },
          exportConfig: {
            fileName: `Export-ElectricalMetrics-${serialNumber}`,
            mapData: (response: GetTableDataResponse): ExportRow[] =>
              response.items.map(item => {
                const csvRow: ExportRow = {
                  dateTime: {
                    title: 'DateTime',
                    value: item.dateTime ? tzService.formatLong(item.dateTime) : ''
                  }
                };
                for (const def of tableData.columnDefs) {
                  const metric = item.metrics.find(m => `${m.aggregate}_${m.kind}_${m.phase}` === def.key);
                  csvRow[def.key] = { title: def.csvTitle, value: metric?.value ?? '' };
                }
                return csvRow;
              })
          }
        } as Pick<ListingPageModel, 'dataSource' | 'exportConfig'>;

        return service.getTableData({ ...exportPayload, skip: 0, take: 1 }).pipe(
          switchMap(({ totalCount }) =>
            exportUtils.getTableDataForExport(
              exportPayload as unknown as ListingPayload,
              listingPageModel as ListingPageModel,
              totalCount
            )
          ),
          tap(data => {
            // getTableDataForExport emits caught errors as data, so only arrays are exportable rows
            if (!Array.isArray(data)) {
              throw new Error('Failed to export');
            }
            exportFacade.exportRows(
              fileType,
              data as ExportRow[],
              null,
              null,
              listingPageModel.exportConfig!.fileName!
            );
          }),
          map(() => MeterElectricalTabContentActions.exportFileSuccess()),
          catchError(() => of(MeterElectricalTabContentActions.exportFileFailure({ error: 'Failed to export CSV' })))
        );
      })
    ),
  { functional: true }
);
